import type { AccountOption } from "@app/components/account-combobox";
import KpiChart from "@app/components/kpi-chart";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import type { loansPage_Query } from "@app/queries/loansPage_Query.graphql";
import type { loansPageTxns_Query } from "@app/queries/loansPageTxns_Query.graphql";
import type { loansPage_FlagMutation } from "@app/queries/loansPage_FlagMutation.graphql";
import type { loansPage_DismissMutation } from "@app/queries/loansPage_DismissMutation.graphql";
import type { loansPage_UpsertMutation } from "@app/queries/loansPage_UpsertMutation.graphql";
import type { loansPage_DeleteMutation } from "@app/queries/loansPage_DeleteMutation.graphql";
import { ChevronDown, ChevronRight, Landmark, Loader2 } from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { commitMutation, fetchQuery, graphql, useLazyLoadQuery, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

export const DEFAULT_PER_YEAR = 10;

export const loansQuery = graphql`
  query loansPage_Query($perYear: Int) {
    loans(perYear: $perYear) {
      debtByMonth {
        month
        value
      }
      loans {
        id
        handle
        name
        label
        principalCents
        feeCents
        totalOwedCents
        holdbackBps
        startDate
        termMonths
        expectedMonthlySalesCents
        payoutAccountNumber
        repaymentMode
        repaymentPattern
        repaymentMatchKind
        repaymentMatchField
        repaymentCaseSensitive
        repaymentEndDate
        repaidCents
        outstandingCents
        fullyRepaid
        proceedsRow {
          postingDate
          description
          amount
          accountNumber
        }
        repaymentRows {
          md5
          postingDate
          description
          amount
          accountNumber
          classification
        }
      }
      candidates {
        year
        rank
        flagged
        loan {
          id
          label
        }
        row {
          txn {
            id
          }
          md5
          postingDate
          description
          amount
          accountNumber
          counterparty {
            name
          }
        }
      }
    }
  }
`;

// Every loan/financing txn in the ledger, on the unified `transactions` resolver:
// the loan classifications UNION the LOAN_PMT kind. This is the catch-all the
// per-loan repayment lists cannot cover — a card payoff, or a LOAN_REPAYMENT a
// rule stamped, belongs to no modelled loan but is still debt service.
const txnsQuery = graphql`
  query loansPageTxns_Query(
    $classifications: [TxnClassificationEnum!]
    $kinds: [String!]
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      classifications: $classifications
      kinds: $kinds
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

const flagMutation = graphql`
  mutation loansPage_FlagMutation($txnId: ID!) {
    loanFlagTxn(txnId: $txnId) {
      loan {
        id
      }
      errors {
        message
      }
    }
  }
`;

const dismissMutation = graphql`
  mutation loansPage_DismissMutation($txnId: ID!, $dismissed: Boolean) {
    loanDismissCandidate(txnId: $txnId, dismissed: $dismissed) {
      dismissed
      errors {
        message
      }
    }
  }
`;

const upsertMutation = graphql`
  mutation loansPage_UpsertMutation($input: LoanUpsertInputObject!) {
    loanUpsert(input: $input) {
      loan {
        id
      }
      errors {
        message
      }
    }
  }
`;

const deleteMutation = graphql`
  mutation loansPage_DeleteMutation($id: ID!) {
    loanDelete(id: $id) {
      deleted
      errors {
        message
      }
    }
  }
`;

type Loans = loansPage_Query["response"]["loans"];
type Loan = Loans["loans"][number];
type Candidate = Loans["candidates"][number];
type LoanInput = loansPage_UpsertMutation["variables"]["input"];

const money = (dollars: number) =>
  dollars.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const moneyCents = (cents: number) => money(cents / 100);
const dollarsFromCents = (cents: number | null | undefined) =>
  cents == null ? "" : (cents / 100).toString();
const centsFromDollars = (value: string) => Math.round(parseFloat(value || "0") * 100);

// Mirrors the loan/financing set the retired /debt page listed.
const LOAN_CLASSIFICATIONS = ["LOAN_PROCEEDS", "LOAN_REPAYMENT", "FINANCING_FEE"] as const;
const LOAN_KINDS = ["LOAN_PMT"] as const;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

const MATCH_KINDS = ["SUBSTRING", "PREFIX", "EXACT", "REGEX"] as const;
const MATCH_FIELDS = [
  { value: "DESCRIPTION", label: "Description" },
  { value: "QBO_COUNTERPARTY", label: "QBO vendor" },
] as const;

export default function LoansPage({
  queryRef,
  perYear,
}: {
  queryRef: PreloadedQuery<loansPage_Query>;
  // The loader's own variable, threaded through so an imperative refresh refetches
  // the SAME query the route loaded rather than silently resetting the depth.
  perYear: number;
}) {
  const preloaded = usePreloadedQuery(loansQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const { loans, candidates, debtByMonth } = data.loans;
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  // Re-sync the local mirror only when the route hands a NEW query, keyed on
  // queryRef — NOT on `preloaded`: refresh() writes the same query root into the
  // store, and keying on `preloaded` would re-emit the stale snapshot and clobber
  // the fresh numbers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  const refresh = () =>
    fetchQuery<loansPage_Query>(environment, loansQuery, { perYear })
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const series = useMemo(
    () => [
      { name: "Outstanding debt", points: debtByMonth, type: "line" as const, color: "#7c3aed" },
    ],
    [debtByMonth]
  );

  const outstandingCurve = debtByMonth.at(-1)?.value ?? 0;
  // An account with no loans still has a debt point for every month — all zero.
  // Charting a flat line at $0 is noise, so the curve only appears once there is
  // debt to draw.
  const hasDebtCurve = debtByMonth.some((point) => (point.value ?? 0) !== 0);
  const borrowed = loans.reduce((sum, loan) => sum + loan.principalCents, 0);
  const owed = loans.reduce((sum, loan) => sum + loan.outstandingCents, 0);

  // Every mutation here changes numbers the whole page derives from (a flag
  // re-classifies a txn, which moves revenue, EBITDA and the debt curve), so they
  // all end by refetching the page query rather than patching the Relay store.
  //
  // ON FAILURE TOO. Each mutation commits its input and THEN re-derives downstream;
  // a failure in that tail (a statement timeout on a large account) still changed
  // the record, so leaving the pre-save view on screen would misreport what the
  // database now holds — precisely what makes a saved pattern look like it matched
  // nothing. Refetching (not reloading) also keeps the error toast readable.
  const settle = (
    errors: readonly { readonly message: string }[] | undefined,
    successMessage: string
  ) => {
    const failure = errors?.[0]?.message;
    toast({ message: failure ?? successMessage, kind: failure ? "error" : "success" });
    refresh().finally(() => setBusy(null));
  };

  const fail = (error: Error) => {
    toast({ message: error.message || "Something went wrong — please try again.", kind: "error" });
    refresh().finally(() => setBusy(null));
  };

  const flag = (candidate: Candidate) => {
    setBusy(`flag:${candidate.row.md5}`);
    commitMutation<loansPage_FlagMutation>(environment, {
      mutation: flagMutation,
      variables: { txnId: candidate.row.txn.id },
      onCompleted: (response, errors) =>
        settle(
          errors ?? response.loanFlagTxn?.errors,
          "Flagged as a loan — it no longer counts as revenue."
        ),
      onError: fail,
    });
  };

  const dismiss = (candidate: Candidate, dismissed: boolean) => {
    setBusy(`dismiss:${candidate.row.md5}`);
    commitMutation<loansPage_DismissMutation>(environment, {
      mutation: dismissMutation,
      variables: { txnId: candidate.row.txn.id, dismissed },
      onCompleted: (response, errors) =>
        settle(
          errors ?? response.loanDismissCandidate?.errors,
          dismissed ? "Removed from the list." : "Back on the list."
        ),
      onError: fail,
    });
  };

  const save = (loan: Loan, input: LoanInput) => {
    setBusy(`save:${loan.id}`);
    commitMutation<loansPage_UpsertMutation>(environment, {
      mutation: upsertMutation,
      variables: { input },
      onCompleted: (response, errors) => settle(errors ?? response.loanUpsert?.errors, "Loan saved."),
      onError: fail,
    });
  };

  const remove = (loan: Loan) => {
    setBusy(`delete:${loan.id}`);
    commitMutation<loansPage_DeleteMutation>(environment, {
      mutation: deleteMutation,
      variables: { id: loan.id },
      onCompleted: (response, errors) =>
        settle(
          errors ?? response.loanDelete?.errors,
          "Loan removed — the deposit counts as revenue again."
        ),
      onError: fail,
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-xl font-bold">Loans</h1>
        <p className="text-sm text-gray-500">
          Money you borrowed, and everything it took to pay it back. A loan is not income: flagging
          one removes it from revenue and from the bottom line, and its repayments stop reading as
          operating cost.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SummaryCard label="Borrowed, all time" value={moneyCents(borrowed)} />
        <SummaryCard label="Still owed" value={moneyCents(owed)} tone="text-violet-700" />
        <SummaryCard label="Debt curve, latest" value={money(outstandingCurve)} tone="text-violet-700" />
      </div>

      {hasDebtCurve && (
        <KpiChart title="Outstanding debt over time" series={series} format="money" onMonthClick={() => {}} />
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Loans taken</h2>
        {loans.length === 0 ? (
          <p className="bg-white rounded border p-6 text-sm text-gray-500">
            No loans yet. Find them in the list of largest inflows below — a loan usually arrives as
            one unusually large, round-numbered deposit.
          </p>
        ) : (
          <div className="space-y-3">
            {loans.map((loan) => (
              <LoanCard
                key={loan.id}
                loan={loan}
                busy={busy}
                onSave={(input) => save(loan, input)}
                onDelete={() => remove(loan)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Largest inflows</h2>
          <p className="text-sm text-gray-500">
            The biggest deposits of each year. Flag the ones that were loans; dismiss the ones that
            were just revenue and they won't come back.
          </p>
        </div>
        {candidates.length === 0 ? (
          <p className="bg-white rounded border p-6 text-sm text-gray-500">Nothing left to triage.</p>
        ) : (
          <CandidateTable candidates={candidates} busy={busy} onFlag={flag} onDismiss={dismiss} />
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">All loan &amp; financing transactions</h2>
          <p className="text-sm text-gray-500">
            Everything classified as debt service, whether or not it belongs to a loan above — card
            payoffs and rule-stamped repayments included.
          </p>
        </div>
        <div className="bg-white rounded border overflow-hidden">
          <Suspense fallback={<p className="p-6 text-sm text-gray-400">Loading transactions…</p>}>
            <LoanTxns />
          </Suspense>
        </div>
      </section>
    </div>
  );
}

// The editable loan-service txn list on the shared shell, carried over from the
// retired /debt page. Edits re-derive server-side; onChanged bumps the shell's own
// fetch key. The loans + curve above are separate chrome and reload on their own
// mutations, so they are not refreshed here.
function LoanTxns() {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<loansPageTxns_Query>(
    txnsQuery,
    {
      classifications: [...LOAN_CLASSIFICATIONS],
      kinds: [...LOAN_KINDS],
      perspectiveId: getPerspectiveId() ?? undefined,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => refresh(),
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  // The export endpoint reads only a single `classification`, not the loan UNION
  // selector, so passing them would export a non-matching (unfiltered) set. The
  // export carries only sort here.
  const exportParams: Record<string, string> = {
    classifications: LOAN_CLASSIFICATIONS.join(","),
    kinds: LOAN_KINDS.join(","),
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        mode="inline"
        interaction="editable"
        title="Loan & financing transactions"
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="Nothing classified as debt service yet — flag a deposit as a loan above."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        renderBulkActions={renderBulkActions}
      />
      {dialogs}
    </>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="bg-white rounded border p-4">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={`text-2xl font-bold ${tone ?? "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function CandidateTable({
  candidates,
  busy,
  onFlag,
  onDismiss,
}: {
  candidates: readonly Candidate[];
  busy: string | null;
  onFlag: (candidate: Candidate) => void;
  onDismiss: (candidate: Candidate, dismissed: boolean) => void;
}) {
  // Grouped by year so the "top N of each year" ranking is legible as such — a
  // flat list would read as one global ranking, which it deliberately is not.
  const years = useMemo(() => {
    const grouped = new Map<number, Candidate[]>();
    candidates.forEach((candidate) => {
      const bucket = grouped.get(candidate.year) ?? [];
      bucket.push(candidate);
      grouped.set(candidate.year, bucket);
    });
    return [...grouped.entries()];
  }, [candidates]);

  return (
    <div className="bg-white rounded border overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {years.map(([year, rows]) => (
            <>
              <tr key={`year-${year}`} className="bg-gray-100">
                <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold text-gray-600">
                  {year}
                </td>
              </tr>
              {rows.map((candidate, index) => {
                const row = candidate.row;
                const flagging = busy === `flag:${row.md5}`;
                const dismissing = busy === `dismiss:${row.md5}`;
                return (
                  <tr
                    key={row.md5}
                    className={`border-t border-gray-100 ${index % 2 ? "bg-gray-50" : "bg-white"} ${
                      candidate.flagged ? "bg-violet-50" : ""
                    }`}
                  >
                    <td className="px-4 py-2 text-gray-400 tabular-nums w-10">{candidate.rank}</td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap w-28">{row.postingDate}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{row.description}</div>
                      <div className="text-xs text-gray-500">
                        {[row.counterparty?.name, row.accountNumber].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums whitespace-nowrap">
                      {money(row.amount)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap w-64">
                      {candidate.flagged ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700">
                          <Landmark size={13} />
                          {candidate.loan?.label ?? "Loan"}
                        </span>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={flagging || dismissing}
                            onClick={() => onFlag(candidate)}
                            className="rounded bg-violet-700 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                          >
                            {flagging ? "Flagging…" : "It's a loan"}
                          </button>
                          <button
                            type="button"
                            disabled={flagging || dismissing}
                            onClick={() => onDismiss(candidate, true)}
                            className="rounded border px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {dismissing ? "…" : "Not a loan"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoanCard({
  loan,
  busy,
  onSave,
  onDelete,
}: {
  loan: Loan;
  busy: string | null;
  onSave: (input: LoanInput) => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: loan.name ?? "",
    principal: dollarsFromCents(loan.principalCents),
    fee: dollarsFromCents(loan.feeCents),
    termMonths: loan.termMonths?.toString() ?? "",
    repaymentMode: loan.repaymentMode,
    holdbackPct: (loan.holdbackBps / 100).toString(),
    expectedMonthlySales: dollarsFromCents(loan.expectedMonthlySalesCents),
    payoutAccountNumber: loan.payoutAccountNumber ?? "",
    repaymentPattern: loan.repaymentPattern ?? "",
    repaymentMatchKind: loan.repaymentMatchKind ?? "SUBSTRING",
    repaymentMatchField: loan.repaymentMatchField ?? "DESCRIPTION",
    repaymentCaseSensitive: loan.repaymentCaseSensitive,
    repaymentEndDate: loan.repaymentEndDate ?? "",
  });

  const saveKey = `save:${loan.id}`;
  const saving = busy === saveKey;
  const deleting = busy === `delete:${loan.id}`;
  const atSource = form.repaymentMode === "AT_SOURCE";
  const progress = loan.totalOwedCents > 0 ? Math.min(1, loan.repaidCents / loan.totalOwedCents) : 0;

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    onSave({
      handle: loan.handle,
      name: form.name.trim() || null,
      principalCents: centsFromDollars(form.principal),
      feeCents: centsFromDollars(form.fee),
      termMonths: form.termMonths ? parseInt(form.termMonths, 10) : null,
      repaymentMode: form.repaymentMode,
      holdbackBps: Math.round(parseFloat(form.holdbackPct || "0") * 100),
      expectedMonthlySalesCents: form.expectedMonthlySales
        ? centsFromDollars(form.expectedMonthlySales)
        : null,
      payoutAccountNumber: form.payoutAccountNumber.trim() || null,
      repaymentPattern: form.repaymentPattern.trim() || null,
      repaymentMatchKind: form.repaymentMatchKind,
      repaymentMatchField: form.repaymentMatchField,
      repaymentCaseSensitive: form.repaymentCaseSensitive,
      repaymentEndDate: form.repaymentEndDate || null,
    });
  };

  return (
    <div className="bg-white rounded border overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        <Landmark size={16} className="text-violet-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 truncate">{loan.label}</div>
          <div className="text-xs text-gray-500">
            {loan.startDate} · {atSource ? "Repaid at source" : "We repay it"} ·{" "}
            {moneyCents(loan.principalCents)} borrowed
            {loan.feeCents > 0 && ` + ${moneyCents(loan.feeCents)} fee`}
          </div>
        </div>
        <div className="w-56 shrink-0">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{moneyCents(loan.repaidCents)} repaid</span>
            <span className={loan.fullyRepaid ? "text-emerald-600 font-medium" : ""}>
              {loan.fullyRepaid ? "Cleared" : `${moneyCents(loan.outstandingCents)} left`}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
            <div
              className={`h-full ${loan.fullyRepaid ? "bg-emerald-500" : "bg-violet-500"}`}
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t bg-gray-50 p-4 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Hit the bank account
            </div>
            {loan.proceedsRow ? (
              <CashRow
                date={loan.proceedsRow.postingDate}
                description={loan.proceedsRow.description}
                account={loan.proceedsRow.accountNumber}
                amount={loan.proceedsRow.amount}
              />
            ) : (
              <p className="text-sm text-gray-500">
                Entered by hand — not linked to a deposit in the ledger.
              </p>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              {atSource ? "Withheld from revenue" : "Repayments"} ({loan.repaymentRows.length})
            </div>
            {loan.repaymentRows.length === 0 ? (
              <p className="text-sm text-gray-500">
                {atSource
                  ? "Nothing simulated yet — set the holdback rate below."
                  : "No repayments matched yet — set the pattern below."}
              </p>
            ) : (
              <div className="bg-white rounded border divide-y divide-gray-100 max-h-72 overflow-y-auto">
                {loan.repaymentRows.map((row) => (
                  <CashRow
                    key={row.md5}
                    date={row.postingDate}
                    description={row.description}
                    account={row.accountNumber}
                    amount={row.amount}
                    hint={row.classification}
                  />
                ))}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="bg-white rounded border p-4 space-y-3">
            <div className="text-sm font-medium text-gray-700">Terms</div>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Name">
                <input className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} />
              </Field>
              <Field label="Borrowed ($)">
                <input className={inputClass} inputMode="decimal" value={form.principal} onChange={(e) => set("principal", e.target.value)} />
              </Field>
              <Field label="Fee / interest ($)">
                <input className={inputClass} inputMode="decimal" value={form.fee} onChange={(e) => set("fee", e.target.value)} />
              </Field>
              <Field label="Term (months)">
                <input className={inputClass} inputMode="numeric" value={form.termMonths} onChange={(e) => set("termMonths", e.target.value)} />
              </Field>
            </div>

            <Field label="How it gets repaid">
              <div className="flex gap-4 pt-0.5">
                {[
                  ["AT_SOURCE", "At the source — taken out of our revenue before we see it (Stripe Capital)"],
                  ["CASH", "We repay it — the repayments are real outflows"],
                ].map(([value, description]) => (
                  <label key={value} className="flex items-start gap-2 text-xs text-gray-600">
                    <input
                      type="radio"
                      className="mt-0.5"
                      checked={form.repaymentMode === value}
                      onChange={() => set("repaymentMode", value)}
                    />
                    <span>{description}</span>
                  </label>
                ))}
              </div>
            </Field>

            {atSource ? (
              <div className="grid grid-cols-3 gap-3">
                <Field label="Holdback (% of revenue)">
                  <input className={inputClass} inputMode="decimal" value={form.holdbackPct} onChange={(e) => set("holdbackPct", e.target.value)} />
                </Field>
                <Field label="Expected monthly revenue ($)" hint="Used only for months past the end of the data">
                  <input className={inputClass} inputMode="decimal" value={form.expectedMonthlySales} onChange={(e) => set("expectedMonthlySales", e.target.value)} />
                </Field>
                <Field label="Account number" hint="Only when there's no deposit to anchor to">
                  <input className={inputClass} value={form.payoutAccountNumber} onChange={(e) => set("payoutAccountNumber", e.target.value)} />
                </Field>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                <Field label="Repayments match" hint="Bounded to on/after the loan date">
                  <input className={inputClass} value={form.repaymentPattern} onChange={(e) => set("repaymentPattern", e.target.value)} placeholder="e.g. BIGBANK LOAN" />
                </Field>
                <Field label="Match kind">
                  <select className={inputClass} value={form.repaymentMatchKind} onChange={(e) => set("repaymentMatchKind", e.target.value)}>
                    {MATCH_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {kind.charAt(0) + kind.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Match against" hint="QBO rows often have no useful description">
                  <select className={inputClass} value={form.repaymentMatchField} onChange={(e) => set("repaymentMatchField", e.target.value)}>
                    {MATCH_FIELDS.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Case sensitive">
                  <label className="flex items-center gap-2 pt-1.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={form.repaymentCaseSensitive}
                      onChange={(e) => set("repaymentCaseSensitive", e.target.checked)}
                    />
                    Match case exactly
                  </label>
                </Field>
                <Field
                  label="Repayments end"
                  hint="Blank = open-ended. Set it when the same lender wrote you more than one facility, or this loan claims the next one's repayments too."
                >
                  <input
                    className={inputClass}
                    type="date"
                    value={form.repaymentEndDate}
                    onChange={(e) => set("repaymentEndDate", e.target.value)}
                  />
                </Field>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                disabled={saving || deleting}
                onClick={onDelete}
                className="text-xs text-red-600 hover:underline disabled:opacity-50"
              >
                {deleting ? "Removing…" : "Not a loan after all — remove"}
              </button>
              <button
                type="submit"
                disabled={saving || deleting}
                className="inline-flex items-center gap-1.5 rounded bg-violet-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? "Saving…" : "Save terms"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputClass = "w-full border rounded px-2 py-1 text-sm";

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-gray-600">{label}</div>
      {hint && <div className="text-[11px] text-gray-400 mb-0.5">{hint}</div>}
      {children}
    </label>
  );
}

function CashRow({
  date,
  description,
  account,
  amount,
  hint,
}: {
  date: string;
  description: string;
  account: string | null | undefined;
  amount: number;
  hint?: string | null;
}) {
  return (
    <div className="flex items-baseline gap-3 px-3 py-1.5 text-sm">
      <span className="text-gray-500 tabular-nums whitespace-nowrap w-24">{date}</span>
      <span className="flex-1 min-w-0 truncate text-gray-900">{description}</span>
      {hint && <span className="text-[11px] text-gray-400 whitespace-nowrap">{hint}</span>}
      <span className="text-gray-400 text-xs whitespace-nowrap">{account ?? ""}</span>
      <span className={`tabular-nums font-medium whitespace-nowrap ${amount < 0 ? "text-gray-900" : "text-emerald-700"}`}>
        {money(amount)}
      </span>
    </div>
  );
}
